import { IDataSourceBackendParam } from "src/app/epicare-public/home/domain/interfaces/samples.interface";

import { CollectionViewer, DataSource } from "@angular/cdk/collections";
import { BehaviorSubject, Observable, Subject, of } from "rxjs";
import { catchError, switchMap, takeUntil, tap } from "rxjs/operators";

/**
 * This abstract class is to be extended by the data sources classes of the tables to be managed by the backend.
 * Only the method protected data(param: DataSourceBackendParam) must be yet implemented.
 */
export abstract class MatTableDataSourceBackendExtended<T> extends DataSource<T> {
  // Behavior subject that contains the last value of the data
  protected dataSubject = new BehaviorSubject<T[]>([]);
  protected dataCountSubject = new BehaviorSubject<number>(0);
  private loadingSubject = new BehaviorSubject<boolean>(false);

  public loading$ = this.loadingSubject.asObservable();
  /** Subject that emits when the component has been destroyed. */
  // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-denylist, id-match
  private onDestroy$ = new Subject<void>();

  public data$ = this.dataSubject.asObservable();

  /**
   * This method will be called once by the Data Table at table bootstrap time.
   * The Data Table expects this method to return an Observable, and the values
   * of that observable contain the data that the Data Table needs to display.
   * It is use automatically by mat-table via dataSource
   * @param collectionViewer provides an Observable that emits information about what data is being
   * displayed (the start index and the end index)
   * @return data that the Data Table needs to display
   */
  connect(_collectionViewer: CollectionViewer): Observable<T[]> {
    return this.dataSubject.asObservable();
  }

  /**
   * This method is called once by the data table at component destruction time.
   * In this method, we are going to complete any observables that we have created
   * internally in this class, in order to avoid memory leaks.
   * We are going to complete both the dataSubject and the loadingSubject,
   * which are then going to trigger the completion of any derived observables.
   * @param collectionViewer provides an Observable that emits information about what data is being
   * displayed (the start index and the end index)
   */
  disconnect(_collectionViewer: CollectionViewer): void {
    this.dataCountSubject.complete();
    this.dataSubject.complete();
    this.loadingSubject.complete();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Method to be implemented that creates an observable that fetches data from the backend.
   * This method should not be launched as such. It just allows you to define an observable
   * that will be launched in the method loadData.
   * @param param parameter send to the route
   * @return Observable emitting the data fetched from the backend
   */
  protected abstract data(param: IDataSourceBackendParam): Observable<T[]>;

  /**
   * Method to be implemented that creates an observable that fetches data count from the backend.
   * This method should not be launched as such. It just allows you to define an observable
   * that will be launched in the method loadData.
   * @param param parameter send to the route
   * @return Observable emitting the total count of data fetched from the backend
   */
  protected abstract dataCount(param: IDataSourceBackendParam): Observable<number>;

  /**
   * This method launch the method data and manage the Behavior Subject to make the connection with the mat-table
   * @param param parameter send to the route
   * @return Promise resolved with the data once it has been loaded
   */
  public loadData(param: IDataSourceBackendParam): Promise<T[]> {
    // begin to load data
    this.loadingSubject.next(true);

    return new Promise<T[]>((resolve) => {
      this.dataCount(param)
        .pipe(
          takeUntil(this.onDestroy$),
          catchError(() => {
            this.dataCountSubject.next(0);
            return of(0);
          }),
          tap((count) => {
            this.dataCountSubject.next(count);
          }),
          switchMap(() =>
            this.data(param).pipe(
              catchError(() => {
                return of([]);
              })
            )
          )
        )
        .subscribe((data) => {
          this.dataSubject.next(data);
          this.loadingSubject.next(false);
          resolve(data);
        });
    });
  }

  /**
   * Resets the data and data count to empty.
   */
  public clearData(): void {
    this.dataCountSubject.next(0);
    this.dataSubject.next([]);
  }

  /**
   * @return The last data emitted by the data source
   */
  public getData(): T[] {
    return this.dataSubject.value;
  }

  /**
   * @return The last data count emitted by the data source
   */
  public getDataCount(): number {
    return this.dataCountSubject.value;
  }
}
